import { Button, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { graphql } from '../../gql'
import { OrderContextProviderQuery, UpdatePurchaseOrderInput } from '../../gql/graphql'
import { currencyFormatter, formatFormInputCurrencyString } from '../../utils/currencyFormatter'

const editMutation = graphql(/* GraphQL */ `
    mutation EditPOFormMuation($id: Int!, $input: UpdatePurchaseOrderInput!) {
      UpdatePurchaseOrder(id: $id, input: $input) {
        id
      }
    }
`)

type TProps = {
  po: NonNullable<NonNullable<Required<OrderContextProviderQuery['Rfq']['quote']>>['purchaseOrder']>
  onCancel: () => void
  onPOUpdated: () => void
}
export default function EditPOForm(props: TProps) {
  const { po, onPOUpdated, onCancel } = props

  const [editPOResult, editPO] = useMutation(editMutation)

  /**
   * Submit the mutation to create the PO
   */
  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (editPOResult.fetching) return
    const amount = Number.parseFloat(data.amount.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(amount)) {
      alert('Amount must be a number')
      return
    }
    if (!data.commission) {
      alert('Commission is required')
      return
    }
    if (Number.isNaN(commission)) {
      alert('Commission must be a number')
      return
    }
    const input: UpdatePurchaseOrderInput = {
      amount,
      commissionPercentage: data.commission,
      poNumber: String(data.poNumber),
      confirmationNumber: data.confirmationNumber,
    }
    if (data.dateReceived) {
      input.dateReceived = data.dateReceived.toISOString()
    }
    if (data.dateRequestedShipment) {
      input.dateRequestedShipment = data.dateRequestedShipment.toISOString()
    }
    if (data.dateEstimatedShipment) {
      input.dateEstimatedShipment = data.dateEstimatedShipment.toISOString()
    }
    const res = await editPO({
      id: po.id,
      input,
    })
    if (res.error) {
      console.error(res.error)
      alert('Error creating purchase order')
    } else {
      onPOUpdated()
    }
  }

  interface IFormInput {
    amount: string
    commission: number
    confirmationNumber: string
    poNumber: string
    dateReceived: Dayjs | null
    dateRequestedShipment?: Dayjs | null
    dateEstimatedShipment?: Dayjs | null
  }

  const { handleSubmit, control, formState, watch, setValue } = useForm<IFormInput>({
    defaultValues: {
      amount: formatFormInputCurrencyString(po.amount.toString()),
      commission: po.commissionPercentage || 2.5,
      confirmationNumber: po.confirmationNumber || '',
      poNumber: po.poNumber || '',
      dateReceived: po.dateReceived ? dayjs(po.dateReceived) : dayjs(),
      dateRequestedShipment: po.dateRequestedShipment ? dayjs(po.dateRequestedShipment) : null,
      dateEstimatedShipment: po.dateEstimatedShipment ? dayjs(po.dateEstimatedShipment) : null,
    },
    mode: 'onBlur',
    disabled: editPOResult.fetching,
  })

  const commission = watch('commission')
  const amount = watch('amount')

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatFormInputCurrencyString(e.target.value)
    if (!formatted) return
    setValue('amount', formatted)
  }

  const handleCommissionBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (typeof v !== 'string') return
    const n = Number.parseFloat(v.replace(/[^0-9.]/g, ''))
    if (!Number.isNaN(n)) {
      setValue('commission', n)
    }
  }

  const estimatedIncome = useMemo<number | undefined>(() => {
    if (!amount || !commission) return undefined
    const n = Number.parseFloat(amount.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(n) || Number.isNaN(commission)) return undefined
    return n * (commission / 100)
  }, [commission, amount])

  const $estimatedIncome = useMemo(() => {
    if (!estimatedIncome) return null
    const formatted = (
      <Typography component="span" color="success" fontSize="inherit" fontWeight="bold">
        {currencyFormatter.format(estimatedIncome)}
      </Typography>
    )

    if (estimatedIncome > 500) {
      return (
        <Typography variant="caption" color="textDisabled">
          Wowzas! You've got {formatted} coming your way.
        </Typography>
      )
    }
    if (estimatedIncome > 50) {
      return (
        <Typography variant="caption" color="textDisabled">
          Nice! This will net you {formatted}!
        </Typography>
      )
    }
  }, [estimatedIncome])

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} onReset={onCancel}>
      <Stack sx={{ ml: 0, mt: 2 }} spacing={4} direction="column" justifyContent="stretch">
        <Controller
          name="amount"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Amount is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Amount must be a number'
              }
              return true
            },
            onBlur: handleAmountBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Amount ($) *"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'The total amount of the purchase order'}
                slotProps={{
                  input: {
                    startAdornment: '$',
                    required: true,
                  },
                }}
              />
            )
          }}
        />
        <Controller
          name="commission"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Field is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Must be a number'
              }
              return true
            },
            onBlur: handleCommissionBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Commission (%) *"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Your commission percentage'}
                slotProps={{
                  input: {
                    endAdornment: '%',
                    required: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="poNumber"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="PO Number"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />

        <Controller
          name="confirmationNumber"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Confirmation Number"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />

        <Controller
          name="dateReceived"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Date Received"
                {...field}
                // disabled={fetching}
                disableFuture
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'The date you received the purchase order',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="dateRequestedShipment"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Requested Shipment Date"
                {...field}
                // disabled={fetching}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'The date your client requested the shipment',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Controller
          name="dateEstimatedShipment"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Estimated Shipment Date"
                {...field}
                // disabled={fetching}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'When your vendor estimates the shipment will be sent',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
          <Button type="reset" variant="outlined" color="primary">
            Cancel
          </Button>
          {$estimatedIncome}
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={editPOResult.fetching || !formState.isValid}
          >
            Submit
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}
